import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { AlertCircle, CheckCircle, Loader2, RefreshCw } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface GapStats {
  totalModules: number;
  modulesWithQuizzes: number;
  modulesWithPractice: number;
  modulesWithShortContent: number;
  coursesWithShortDescriptions: number;
  certificatesEnabled: number;
  totalCourses: number;
  lastChecked: string;
}

export default function ContentGapMonitor() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<GapStats | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Get all statistics in parallel
      const [
        modulesResult,
        quizzesResult,
        practiceResult,
        shortContentResult,
        shortDescResult,
        coursesResult,
        certSettingsResult
      ] = await Promise.all([
        supabase.from('module_content').select('id', { count: 'exact', head: true }),
        supabase.from('module_content').select('id', { count: 'exact', head: true }).eq('has_quiz', true),
        supabase.from('module_content').select('id', { count: 'exact', head: true }).eq('has_practice', true),
        supabase.from('module_content').select('id', { count: 'exact', head: true }).lt('content_word_count', 200),
        supabase.from('courses').select('id', { count: 'exact', head: true }).eq('is_active', true).lt('LENGTH(description)', 80),
        supabase.from('courses').select('id', { count: 'exact', head: true }).eq('is_active', true),
        supabase.from('course_settings').select('id', { count: 'exact', head: true }).eq('certificate_enabled', true)
      ]);

      setStats({
        totalModules: modulesResult.count || 0,
        modulesWithQuizzes: quizzesResult.count || 0,
        modulesWithPractice: practiceResult.count || 0,
        modulesWithShortContent: shortContentResult.count || 0,
        coursesWithShortDescriptions: shortDescResult.count || 0,
        totalCourses: coursesResult.count || 0,
        certificatesEnabled: certSettingsResult.count || 0,
        lastChecked: new Date().toLocaleTimeString()
      });

    } catch (error) {
      console.error('Error fetching stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch gap statistics',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // Refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleGenerateQuizzes = async () => {
    setProcessing('quizzes');
    try {
      const { data, error } = await supabase.functions.invoke('bulk-generate-quizzes', {
        body: { action: 'start' }
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Started generating quizzes for ${data.totalModules} modules`,
      });

      fetchStats();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setProcessing(null);
    }
  };

  const handleEnhanceDescriptions = async () => {
    setProcessing('descriptions');
    try {
      const { data, error } = await supabase.functions.invoke('enhance-course-descriptions');

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Enhanced ${data.enhanced} course descriptions`,
      });

      fetchStats();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setProcessing(null);
    }
  };

  const handleEnhanceContent = async () => {
    setProcessing('content');
    try {
      const { data, error } = await supabase.functions.invoke('bulk-enhance', {
        body: { action: 'start', targetWordCount: 1200 }
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Started enhancing ${data.totalModules} modules`,
      });

      fetchStats();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!stats) return null;

  const quizCoverage = (stats.modulesWithQuizzes / stats.totalModules) * 100;
  const practiceCoverage = (stats.modulesWithPractice / stats.totalModules) * 100;
  const contentQuality = ((stats.totalModules - stats.modulesWithShortContent) / stats.totalModules) * 100;
  const descriptionQuality = ((stats.totalCourses - stats.coursesWithShortDescriptions) / stats.totalCourses) * 100;
  const certCoverage = (stats.certificatesEnabled / stats.totalCourses) * 100;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Content Gap Monitor</h1>
          <p className="text-muted-foreground mt-1">
            Real-time tracking of content completeness
          </p>
        </div>
        <Button onClick={fetchStats} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Quiz Coverage */}
        <Card className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-semibold text-lg">Quiz Coverage</h3>
              <p className="text-2xl font-bold mt-2">{Math.round(quizCoverage)}%</p>
            </div>
            {quizCoverage >= 80 ? (
              <CheckCircle className="h-6 w-6 text-green-500" />
            ) : (
              <AlertCircle className="h-6 w-6 text-orange-500" />
            )}
          </div>
          <Progress value={quizCoverage} className="mb-3" />
          <p className="text-sm text-muted-foreground mb-4">
            {stats.modulesWithQuizzes} of {stats.totalModules} modules have quizzes
          </p>
          <Button 
            onClick={handleGenerateQuizzes}
            disabled={processing === 'quizzes' || quizCoverage >= 95}
            size="sm"
            className="w-full"
          >
            {processing === 'quizzes' ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              'Generate Missing Quizzes'
            )}
          </Button>
        </Card>

        {/* Practice Lab Coverage */}
        <Card className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-semibold text-lg">Practice Labs</h3>
              <p className="text-2xl font-bold mt-2">{Math.round(practiceCoverage)}%</p>
            </div>
            {practiceCoverage >= 80 ? (
              <CheckCircle className="h-6 w-6 text-green-500" />
            ) : (
              <AlertCircle className="h-6 w-6 text-orange-500" />
            )}
          </div>
          <Progress value={Math.min(practiceCoverage, 100)} className="mb-3" />
          <p className="text-sm text-muted-foreground">
            {stats.modulesWithPractice} of {stats.totalModules} modules have labs
          </p>
        </Card>

        {/* Content Quality */}
        <Card className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-semibold text-lg">Content Quality</h3>
              <p className="text-2xl font-bold mt-2">{Math.round(contentQuality)}%</p>
            </div>
            {contentQuality >= 95 ? (
              <CheckCircle className="h-6 w-6 text-green-500" />
            ) : (
              <AlertCircle className="h-6 w-6 text-orange-500" />
            )}
          </div>
          <Progress value={contentQuality} className="mb-3" />
          <p className="text-sm text-muted-foreground mb-4">
            {stats.modulesWithShortContent} modules need enhancement
          </p>
          <Button 
            onClick={handleEnhanceContent}
            disabled={processing === 'content' || contentQuality >= 99}
            size="sm"
            className="w-full"
          >
            {processing === 'content' ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Enhancing...
              </>
            ) : (
              'Enhance Short Content'
            )}
          </Button>
        </Card>

        {/* Course Descriptions */}
        <Card className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-semibold text-lg">Course Descriptions</h3>
              <p className="text-2xl font-bold mt-2">{Math.round(descriptionQuality)}%</p>
            </div>
            {descriptionQuality >= 95 ? (
              <CheckCircle className="h-6 w-6 text-green-500" />
            ) : (
              <AlertCircle className="h-6 w-6 text-orange-500" />
            )}
          </div>
          <Progress value={descriptionQuality} className="mb-3" />
          <p className="text-sm text-muted-foreground mb-4">
            {stats.coursesWithShortDescriptions} courses need better descriptions
          </p>
          <Button 
            onClick={handleEnhanceDescriptions}
            disabled={processing === 'descriptions' || descriptionQuality >= 95}
            size="sm"
            className="w-full"
          >
            {processing === 'descriptions' ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Enhancing...
              </>
            ) : (
              'Enhance Descriptions'
            )}
          </Button>
        </Card>

        {/* Certificate Coverage */}
        <Card className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-semibold text-lg">Certificates</h3>
              <p className="text-2xl font-bold mt-2">{Math.round(certCoverage)}%</p>
            </div>
            {certCoverage >= 80 ? (
              <CheckCircle className="h-6 w-6 text-green-500" />
            ) : (
              <AlertCircle className="h-6 w-6 text-orange-500" />
            )}
          </div>
          <Progress value={certCoverage} className="mb-3" />
          <p className="text-sm text-muted-foreground">
            {stats.certificatesEnabled} of {stats.totalCourses} courses enabled
          </p>
        </Card>

        {/* Last Updated */}
        <Card className="p-6 flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-2">Last Updated</p>
            <p className="text-xl font-semibold">{stats.lastChecked}</p>
            <p className="text-xs text-muted-foreground mt-2">Auto-refreshes every 30s</p>
          </div>
        </Card>
      </div>
    </div>
  );
}